import React, { useEffect, useState } from "react";
import { Col, Form } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { v4 as uuidv4 } from "uuid";
import { FieldProps } from "././../../../../../../../InputFieldsController/InputFieldsController.utils";

export interface SelectOption {
  id: string;
  label: string;
  value: string;
  groupKey?: string;
}

interface SelectOptionRendererProps {
  selectedField: FieldProps;
  updateField: (updates: Partial<FieldProps>) => void;
}

/**
 * SelectOptionRenderer
 * Manages dropdown options with optional cascading groupKey support.
 */
const SelectOptionRenderer: React.FC<SelectOptionRendererProps> = ({
  selectedField,
  updateField,
}) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isCascading, setIsCascading] = useState<boolean>(false);

  useEffect(() => {
    console.log("selectedField: ", selectedField)
    const opts = selectedField.options || [];
    const normalized = opts.map((opt: any) => ({
      id: opt.id || uuidv4(),
      label: opt.label || opt.value || "",
      value: opt.value || opt.label || "",
      groupKey: opt.groupKey || "",
    }));
    setOptions(normalized);
  }, [selectedField]);

  const handleUpdateOptions = (updated: SelectOption[]) => {
    const finalOptions = isCascading
      ? updated
      : updated.map(({ groupKey, ...rest }) => rest); // strip groupKey if not cascading

    setOptions(finalOptions);
    updateField({
      options: finalOptions,
      allOptions: updated, // always store full set
    });
  };

  const handleAddOption = () => {
    const newOption: SelectOption = {
      id: uuidv4(),
      label: "",
      value: "",
      ...(isCascading ? { groupKey: "" } : {}),
    };
    handleUpdateOptions([...options, newOption]);
  };

  const handleRemoveOption = (id: string) => {
    handleUpdateOptions(options.filter((opt) => opt.id !== id));
  };

  const handleChange = (id: string, changes: Partial<SelectOption>) => {
    const updated = options.map((opt) =>
      opt.id === id ? { ...opt, ...changes } : opt
    );
    handleUpdateOptions(updated);
  };

  return (
    <Form className="no-padding">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="mb-0">Dropdown Options</p>
        <div className="d-flex align-items-center gap-2">
          <Form.Check
            type="switch"
            id="toggle-cascading"
            label="Cascading"
            checked={isCascading}
            onChange={() => setIsCascading((prev) => !prev)}
          />
          <Icon.PlusCircleFill
            style={{
              fontSize: 20,
              color: "var(--theme1_primary)",
              cursor: "pointer",
            }}
            onClick={handleAddOption}
          />
        </div>
      </div>

      {options.map((opt, index) => (
        <div
          key={opt.id}
          className="d-flex align-items-start mb-2"
          style={{ gap: 10 }}
        >
          <Icon.Square
            style={{
              fontSize: 18,
              marginTop: 8,
              color: "var(--theme1_primary)",
            }}
          />
          <Col>
            <Form.Control
              size="sm"
              type="text"
              placeholder={`Label ${index + 1}`}
              value={opt.label}
              onChange={(e) =>
                handleChange(opt.id, {
                  label: e.target.value,
                  value: e.target.value,
                })
              }
              className="custom-input mb-1"
            />
            {isCascading && (
              <Form.Control
                size="sm"
                type="text"
                placeholder="Group Key (e.g., State Code)"
                value={opt.groupKey || ""}
                onChange={(e) =>
                  handleChange(opt.id, { groupKey: e.target.value })
                }
                className="custom-input mb-1"
              />
            )}
          </Col>
          <Icon.DashCircleFill
            style={{
              fontSize: 20,
              color: "var(--theme1_primary)",
              cursor: "pointer",
              marginTop: 8,
            }}
            onClick={() => handleRemoveOption(opt.id)}
          />
        </div>
      ))}
    </Form>
  );
};

export default SelectOptionRenderer;
